import React, { useMemo } from 'react'
import { Elements, ExpressCheckoutElement } from '@stripe/react-stripe-js'

import BackLink from '../BackLink'
import DataSharing from '../DataSharing'
import DonationExtras from '../DonationExtras'
import PageHeader from '../PageHeader'
import Tipping from '../Tipping'
import stripeAppearance from '../stripeAppearance'
import usePayment from '../usePayment'
import { toStripeMinorUnits } from '../stripeIntents'
import { buildLineItems, businessName } from '../lineItems'

// Final payment step for apple_pay / google_pay. Mounts a dedicated ECE
// constrained to the wallet the donor picked on PaymentMethodStep —
// only that one button renders, opening the corresponding wallet sheet
// on click. The discovery ECE on App.jsx is a separate instance that
// only listens for onReady; this one actually carries the donation
// through stripe.confirmPayment.
//
// No Contact step runs ahead of this — the wallet supplies billing
// details (name, email, phone) on its own confirm sheet, which we read
// from event.billingDetails on onConfirm. Tipping still renders above
// the button (when applicable) so the donor can adjust tip /
// cover-costs before paying.
const ExpressCheckout = ({
  i18n, donation, plugins, record, partnerItem,
  paymentMethod, creditCardProps, collect_phone, collect_address,
  foundation, partner, partner_is_pledge, data_sharing, defaultShares,
  extras, blank_modal_src, emoji_picker_loader_src, widget_instance_token,
  stripePromise, stripeFonts,
  tippingEnabled, coverCostsEnabled, pledgeCoversFees,
  finished, setSubmitInFlight, onBack,
}) => {
  // Recurring donations route through SetupIntent; the ECE Elements
  // scope must match so the wallet sheet shows the correct mandate copy
  // (Apple/Google Pay surface "Subscribe / will charge $X / month"
  // language only when mode is 'subscription').
  const isSubscription = donation.state.subscription !== 'one_time'
  const elementsOptions = useMemo(() => ({
    mode: isSubscription ? 'subscription' : 'payment',
    // Stripe rejects amount: 0 on mount; floor at 1 minor unit.
    amount: Math.max(toStripeMinorUnits(donation.total, donation.currency), 1),
    currency: donation.currency.iso_code.toLowerCase(),
    paymentMethodTypes: [ 'card' ],
    appearance: stripeAppearance(),
    fonts: stripeFonts,
    locale: i18n.locale,
  }), [
    isSubscription, donation.total,
    donation.currency.iso_code, donation.currency.exponent,
    stripeFonts, i18n.locale,
  ])

  return (
    <Elements
      key={donation.currency.iso_code}
      stripe={stripePromise}
      options={elementsOptions}
    >
      <ExpressCheckoutForm
        i18n={i18n}
        donation={donation}
        plugins={plugins}
        record={record}
        partnerItem={partnerItem}
        paymentMethod={paymentMethod}
        creditCardProps={creditCardProps}
        collect_phone={collect_phone}
        collect_address={collect_address}
        foundation={foundation}
        partner={partner}
        partner_is_pledge={partner_is_pledge}
        data_sharing={data_sharing}
        defaultShares={defaultShares}
        extras={extras}
        blank_modal_src={blank_modal_src}
        emoji_picker_loader_src={emoji_picker_loader_src}
        widget_instance_token={widget_instance_token}
        tippingEnabled={tippingEnabled}
        coverCostsEnabled={coverCostsEnabled}
        pledgeCoversFees={pledgeCoversFees}
        finished={finished}
        setSubmitInFlight={setSubmitInFlight}
        onBack={onBack}
      />
    </Elements>
  )
}

const ExpressCheckoutForm = ({
  i18n, donation, plugins, record, partnerItem,
  paymentMethod, creditCardProps, collect_phone, collect_address,
  foundation, partner, partner_is_pledge, data_sharing, defaultShares,
  extras, blank_modal_src, emoji_picker_loader_src, widget_instance_token,
  tippingEnabled, coverCostsEnabled, pledgeCoversFees,
  finished, setSubmitInFlight, onBack,
}) => {
  const payment = usePayment({
    i18n, donation, record, partnerItem,
    paymentMethod, creditCardProps,
    finished, setSubmitInFlight,
  })

  // Constrain the rendered button to the wallet the donor picked. The
  // 'never' side stays mounted but invisible — Stripe needs both keys
  // present in the options object even when one is hidden.
  const eceOptions = {
    paymentMethods: {
      applePay:  paymentMethod === 'apple_pay'  ? 'always' : 'never',
      googlePay: paymentMethod === 'google_pay' ? 'always' : 'never',
    },
    layout: { overflow: 'never' },
    // Match v7's regular .btn height: 8px padding × 2 + 24px line-height
    // + 2px border × 2 = 44px. Stripe accepts 36-55, so 44 fits cleanly
    // and reads consistent with the credit_card / paypal / bank submit
    // buttons next to this one on the picker.
    buttonHeight: 44,
    // Apple Pay / Google Pay only allow the "Donate" button face for
    // verified 501(c)(3) merchants — fall back to plain otherwise.
    buttonType: {
      applePay:  foundation ? 'donate' : 'plain',
      googlePay: foundation ? 'donate' : 'plain',
    },
  }

  // onClick fires before the wallet sheet opens. resolve() hands Stripe
  // the line items + business name to display, plus the contact fields
  // we want the wallet to surface. emailRequired is always true (we
  // need it for the receipt); phone is gated on partner config.
  //
  // Gate the resolve on tip validation — wallets skip the Contact step,
  // so this is our only chance to surface the tip error before Stripe
  // takes over the donor's screen with the wallet sheet. validateTip
  // no-ops when tipping isn't engaged.
  const handleEceClick = ({ resolve }) => {
    // Wallets skip the Contact step, so this click — before the wallet
    // sheet takes over the screen — is the only chance to surface tip
    // and plugin (afterDataSharing) errors. Run both regardless, then
    // bail without resolving if either failed.
    const tipOk = donation.validateTip()
    const pluginsOk = plugins.validateSlots([ 'afterDataSharing' ], { donation })
    if (!tipOk || !pluginsOk) return
    resolve({
      business: { name: businessName({ creditCardProps, partnerItem, i18n }) },
      emailRequired: true,
      phoneNumberRequired: !!collect_phone,
      // Always require the billing address for wallets — it's the ONLY
      // way to get the donor's name out of the ExpressCheckoutElement.
      // Unlike v6's PaymentRequest (requestPayerName), the ECE has no
      // name-only option (see @stripe/stripe-js express-checkout types:
      // billingAddressRequired / emailRequired / phoneNumberRequired,
      // but no nameRequired); the name only comes back bundled with the
      // billing address. Wallets skip the Contact step, so without this
      // the event carries no name and draft creation fails
      // (user_first_name can't be blank). Apple/Google Pay return the
      // billing contact from what's already on file, so this is
      // frictionless — and since the donor's provided it, the address
      // rides along onto the order too (submitExpressCheckout).
      billingAddressRequired: true,
      lineItems: buildLineItems({ i18n, donation }),
    })
  }

  // onConfirm fires after the donor approves payment in the wallet
  // sheet. usePayment reads expressPaymentType + billingDetails off the
  // event (expressPaymentType is the runtime property name; the npm
  // types call it paymentMethodType, which is undefined at runtime —
  // see the ECE event-property memory note) and calls
  // event.paymentFailed if anything bails before
  // stripe.confirmPayment, so it owns the whole event lifecycle.
  const handleEceConfirm = event => payment.submitExpressCheckout(event)

  const errorMessage = payment.errors?.base?.[0]

  return (
    <div className="space-y-4">
      <PageHeader heading={i18n.t('steps.payment.heading')} />

      <div className="space-y-2">
        <DataSharing
          i18n={i18n}
          foundation={foundation}
          partner_is_pledge={partner_is_pledge}
          data_sharing={data_sharing}
          collect_address={collect_address}
          collect_phone={collect_phone}
          beneficiary={partnerItem.beneficiary}
          partner={partner}
          defaultShares={defaultShares}
          shares={donation.shares}
          setShares={donation.setShares}
        />
        {plugins.renderSlot('afterDataSharing', { donation })}
        <DonationExtras
          extras={extras}
          i18n={i18n}
          blank_modal_src={blank_modal_src}
          emoji_picker_loader_src={emoji_picker_loader_src}
          widget_instance_token={widget_instance_token}
        />
      </div>

      {/* Donation amount + total are rendered by the wallet sheet
          itself; Tipping only carries the optional tipping and
          cover-costs selectors and self-suppresses to nothing when
          neither feature applies. */}
      <Tipping
        i18n={i18n}
        donation={donation}
        partnerItem={partnerItem}
        tippingEnabled={tippingEnabled}
        coverCostsEnabled={coverCostsEnabled}
        pledgeCoversFees={pledgeCoversFees}
      />

      {errorMessage && (
        <p
          role="alert"
          className="rounded border border-danger-300 bg-danger-50 text-danger-800 p-3 text-sm wrap-break-word"
        >
          {errorMessage}
        </p>
      )}

      <div className="space-y-2">
        <ExpressCheckoutElement
          options={eceOptions}
          onClick={handleEceClick}
          onConfirm={handleEceConfirm}
        />
        <BackLink i18n={i18n} onClick={onBack} disabled={payment.processing} />
      </div>
    </div>
  )
}

export default ExpressCheckout
